import { useState, useEffect } from "react";
import { ChevronUp, Mail, ExternalLink, HelpCircle, ChevronDown } from "lucide-react";

const faqs = [
  {
    id: "cancel-subscription",
    question: "How do I cancel my subscription?",
    answer: (
      <>
        <p>
          All subscriptions are managed through your Apple ID, not directly within the app.
          To cancel:
        </p>
        <ol className="pp-ordered-list">
          <li>Open the Settings app on your iPhone</li>
          <li>Tap your name at the top</li>
          <li>Tap Subscriptions</li>
          <li>Select Hearing Pro</li>
          <li>Tap Cancel Subscription</li>
        </ol>
        <p>
          Your Pro features will remain active until the end of your current billing period.
        </p>
      </>
    ),
  },
  {
    id: "request-refund",
    question: "How do I request a refund?",
    answer: (
      <p>
        Refunds for App Store purchases are handled by Apple, not by us. You can request a
        refund at{" "}
        <a
          href="https://reportaproblem.apple.com"
          target="_blank"
          rel="noopener noreferrer"
          className="pp-link"
          data-testid="faq-refund-link"
        >
          reportaproblem.apple.com
          <ExternalLink size={12} className="pp-external-icon" />
        </a>{" "}
        or through Apple Support.
      </p>
    ),
  },
  {
    id: "restore-purchase",
    question: "How do I restore a previous purchase?",
    answer: (
      <p>
        If you purchased Hearing Pro on another device (or reinstalled the app), open Settings
        inside Hearing Decibel Pro and tap <strong>Restore Purchases</strong>. Make sure you're
        signed in with the same Apple ID used for the original purchase.
      </p>
    ),
  },
  {
    id: "decibel-accuracy",
    question: "The decibel meter readings don't match my sound-level meter app. Why?",
    answer: (
      <p>
        Hearing Decibel Pro uses your iPhone's built-in microphone, which is not calibrated for
        laboratory-grade accuracy. Readings may differ from professional sound-level meters by
        several decibels. Use the app's readings as an estimate, not for regulatory, legal, or
        occupational safety decisions.
      </p>
    ),
  },
  {
    id: "diagnose-hearing-loss",
    question: "Can Hearing Decibel Pro diagnose hearing loss?",
    answer: (
      <p>
        No. Hearing Decibel Pro is a screening and educational tool, not a medical device. If
        you are concerned about your hearing, please consult a licensed audiologist or qualified
        healthcare provider. A clinical hearing evaluation is much more accurate than any
        consumer app.
      </p>
    ),
  },
  {
    id: "strange-results",
    question: "My hearing test results look strange. What should I do?",
    answer: (
      <p>
        Hearing tests work best in a quiet room using over-ear or in-ear headphones (not
        external speakers). Ambient noise, device speakers, or non-sealed earbuds can produce
        inaccurate results. Try testing in a quieter environment with proper headphones. If
        results still seem off, contact us at the email above.
      </p>
    ),
  },
  {
    id: "ipad-mac",
    question: "Does the app work on iPad or Mac?",
    answer: (
      <p>
        Hearing Decibel Pro is currently designed for iPhone running iOS 17 or later. It is not
        optimized for iPad or Mac.
      </p>
    ),
  },
  {
    id: "sync-devices",
    question: "Will Hearing Decibel Pro sync to my other iPhone?",
    answer: (
      <p>
        Yes, if you sign in with Apple. Your subscription (Hearing Pro access) automatically
        syncs across devices using the same Apple ID. Your hearing test history and noise
        exposure data are currently stored locally on each device — cross-device data sync is
        planned for a future update.
      </p>
    ),
  },
  {
    id: "apple-health",
    question: "Does the app use Apple Health?",
    answer: (
      <p>
        Optionally, yes. If you grant permission, Hearing Decibel Pro can write your hearing
        test results and noise exposure data to the Apple Health app. It does not read your
        Health data. You can enable or revoke this permission at any time in iOS Settings
        &gt; Privacy &amp; Security &gt; Health.
      </p>
    ),
  },
];

const FaqItem = ({ faq }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="sp-faq-item" data-testid={`faq-item-${faq.id}`}>
      <button
        className={`sp-faq-question ${open ? "open" : ""}`}
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${faq.id}`}
        aria-expanded={open}
      >
        <span>{faq.question}</span>
        <ChevronDown size={18} className={`sp-faq-chevron ${open ? "rotated" : ""}`} />
      </button>
      {open && (
        <div className="sp-faq-answer" data-testid={`faq-answer-${faq.id}`}>
          {faq.answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="support-page">
      <main className="pp-main" data-testid="support-content">
        <article className="pp-article">
          {/* Header */}
          <header className="pp-header" data-testid="support-header">
            <div className="pp-header-icon pp-header-icon--support">
              <HelpCircle size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Support</h1>
            <p className="pp-app-name">Hearing Decibel Pro</p>
            <p className="sp-subtitle">
              Need help with Hearing Decibel Pro? You're in the right place.
            </p>
          </header>

          {/* Contact Us */}
          <section id="contact-us" className="pp-section" data-testid="section-support-contact">
            <h2 className="pp-section-heading">Contact Us</h2>
            <p>The fastest way to reach us is by email:</p>
            <div className="sp-email-card" data-testid="support-email-card">
              <a
                href="mailto:support@radships.com"
                className="sp-email-link"
                data-testid="support-email-link"
              >
                <Mail size={20} />
                support@radships.com
              </a>
            </div>
            <p>
              We typically respond within <strong>2 business days</strong>. For billing or
              subscription issues, please include your Apple ID email address so we can help you
              faster.
            </p>
          </section>

          {/* FAQs */}
          <section id="common-questions" className="pp-section" data-testid="section-faqs">
            <h2 className="pp-section-heading">Common Questions</h2>
            <div className="sp-faq-list" data-testid="faq-list">
              {faqs.map((faq) => (
                <FaqItem key={faq.id} faq={faq} />
              ))}
            </div>
          </section>

          {/* Privacy and Terms */}
          <section id="privacy-terms" className="pp-section" data-testid="section-privacy-terms">
            <h2 className="pp-section-heading">Privacy and Terms</h2>
            <p>For details on data handling and terms of use:</p>
            <div className="sp-links-row" data-testid="legal-links">
              <a
                href="/hearingdecibelpro/privacy"
                className="sp-legal-link"
                data-testid="support-privacy-link"
              >
                Privacy Policy
              </a>
              <a
                href="/hearingdecibelpro/terms"
                className="sp-legal-link"
                data-testid="support-terms-link"
              >
                Terms of Service
              </a>
            </div>
          </section>

          {/* About */}
          <section id="about" className="pp-section" data-testid="section-about">
            <h2 className="pp-section-heading">About</h2>
            <p>
              Hearing Decibel Pro is developed and maintained by{" "}
              <strong>Radhesh Gowd</strong>, an independent iOS developer.
            </p>
          </section>

          {/* Footer */}
          <footer className="pp-footer" data-testid="support-footer">
            <p>&copy; {new Date().getFullYear()} Radhesh Gowd. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {/* Back to top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="support-back-to-top-button"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default Support;
